import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Copy, Check, Landmark, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";
import { fmtPrice } from "@/lib/format";

// Banques partenaires Interac au Canada (pages de connexion publiques + logos officiels locaux)
const BANKS = [
  { name: "Desjardins AccèsD", url: "https://accesd.desjardins.com", logo: "/images/banks/desjardins.svg" },
  { name: "RBC Banque Royale", url: "https://www.rbcroyalbank.com", logo: "/images/banks/rbc.svg" },
  { name: "TD EasyWeb", url: "https://easyweb.td.com", logo: "/images/banks/td.svg" },
  { name: "BMO", url: "https://www.bmo.com", logo: "/images/banks/bmo.svg" },
  { name: "Banque Scotia", url: "https://www.scotiabank.com", logo: "/images/banks/scotia.svg" },
  { name: "CIBC", url: "https://www.cibc.com", logo: "/images/banks/cibc.svg" },
  { name: "Banque Nationale", url: "https://www.bnc.ca", logo: "/images/banks/nationale.svg" },
  { name: "Tangerine", url: "https://www.tangerine.ca", logo: "/images/banks/tangerine.svg" },
  { name: "Simplii Financial", url: "https://www.simplii.com", logo: "/images/banks/simplii.svg" },
  { name: "Manuvie", url: "https://www.manuvie.ca", logo: "/images/banks/manuvie.svg" },
];

const RECIPIENT = "payments@nordiguard.com";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const CopyField = ({ label, value, mono = false, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      className="group flex w-full items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3.5 text-left transition-all duration-300 hover:border-sky-500/40"
    >
      <span className="min-w-0">
        <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</span>
        <span className={`mt-0.5 block truncate text-sm font-bold text-slate-100 ${mono ? "font-mono tracking-[0.2em]" : ""}`}>
          {value}
        </span>
      </span>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all ${
        copied ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-400 group-hover:text-sky-300"
      }`}>
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </span>
    </button>
  );
};

export default function InteracTransfer() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const code = params.get("code");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId || !code) {
      setLoading(false);
      return;
    }
    api
      .get(`/orders/${orderId}?code=${code}`)
      .then((r) => setOrder(r.data.order))
      .catch(() => toast.error(t("success.notFound")))
      .finally(() => setLoading(false));
  }, [orderId, code]);

  if (loading) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center pt-24">
        <Loader2 size={36} className="animate-spin text-sky-400" />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-4 pt-32 pb-24 sm:px-8" data-testid="interac-page">
      <Seo title="Virement Interac | NordiGuard" description="Instructions de virement Interac NordiGuard" />
      {order ? (
        <div>
          <motion.div {...fadeUp(0)} className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">
              <Landmark size={13} /> {t("interac.title")}
            </span>
            <h1 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">
              {t("interac.subtitle")}
            </h1>
          </motion.div>

          {/* Étape 1 : banques partenaires */}
          <motion.section {...fadeUp(0.12)} className="mt-12">
            <h2 className="font-display text-lg font-bold text-slate-50">{t("interac.stepBank")}</h2>
            <p className="mt-1.5 text-sm text-slate-400">{t("interac.stepBankSub")}</p>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="interac-banks">
              {BANKS.map((b) => (
                <a
                  key={b.name}
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`interac-bank-${b.name.split(" ")[0].toLowerCase()}`}
                  className="group flex flex-col items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-sky-500/40 hover:bg-slate-900/70"
                >
                  <span className="flex h-14 w-full items-center justify-center rounded-lg bg-white px-3 py-2 transition-transform duration-300 group-hover:scale-[1.03]">
                    <img
                      src={b.logo}
                      alt={`Logo ${b.name}`}
                      loading="lazy"
                      className="max-h-10 max-w-full object-contain"
                      data-testid={`interac-logo-${b.name.split(" ")[0].toLowerCase()}`}
                    />
                  </span>
                  <span className="text-center">
                    <span className="block text-xs font-bold text-slate-100">{b.name}</span>
                    <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 transition-colors group-hover:text-sky-400">
                      {lang === "en" ? "Sign in" : "Connexion"} →
                    </span>
                  </span>
                </a>
              ))}
            </div>
          </motion.section>

          {/* Étape 2 : détails du virement */}
          <motion.section {...fadeUp(0.24)} className="mt-10">
            <h2 className="font-display text-lg font-bold text-slate-50">{t("interac.stepSend")}</h2>
            <div className="mt-5 space-y-3">
              <CopyField label={t("interac.sendTo")} value={RECIPIENT} testId="interac-copy-recipient" />
              <CopyField label={t("interac.sendAmount")} value={fmtPrice(order.total, lang)} testId="interac-copy-amount" />
              <CopyField label={t("interac.sendMessage")} value={order.confirmation_code} mono testId="interac-copy-code" />
            </div>
          </motion.section>

          {/* Étape 3 */}
          <motion.section {...fadeUp(0.36)} className="glacial-card mt-10 p-6 text-center">
            <h2 className="font-display text-lg font-bold text-slate-50">{t("interac.stepDone")}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{t("interac.stepDoneSub")}</p>
            <Link
              to={`/paiement/succes?order_id=${orderId}&code=${code}`}
              data-testid="interac-confirm-cta"
              className="group mt-5 inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-3.5 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_30px_rgba(56,189,248,0.5)]"
            >
              {t("interac.confirmCta")}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.section>

          <motion.p {...fadeUp(0.44)} className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-slate-500">
            <ShieldCheck size={14} className="shrink-0 text-sky-400" />
            {t("interac.secureNote")}
          </motion.p>
        </div>
      ) : (
        <p className="pt-16 text-center text-sm text-slate-400" data-testid="interac-not-found">{t("success.notFound")}</p>
      )}
    </main>
  );
}
